import { Link } from "react-router-dom";
import { ShieldCheck, MessageSquare, ArrowLeft } from "lucide-react";

/**
 * Public legal page used by Twilio reviewers to validate the SMS opt-in
 * workflow (error 30509 fix). Lives at /sms-terms — no login required.
 *
 * Content tailored to the Mueblería Canales internal audit app: employees are
 * opted-in by the business owner / admin when their phone is captured in the
 * Users page; messages are operational (audit alerts, TO DOs, reminders), not
 * marketing.
 */
export default function SmsTermsPage() {
  return (
    <div className="min-h-screen bg-stone-50">
      <header className="bg-[#0B1220] text-white py-10 px-6">
        <div className="max-w-3xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs text-stone-300 hover:text-white mb-4"
            data-testid="back-to-app"
          >
            <ArrowLeft size={12} /> Back to app
          </Link>
          <div className="canales-overline text-stone-300 flex items-center gap-2">
            <MessageSquare size={12} /> SMS Program · Terms & Opt-In
          </div>
          <h1 className="font-display text-3xl md:text-5xl mt-2 leading-[1.05]">
            How our SMS notifications work
          </h1>
          <p className="text-stone-300 mt-3 max-w-2xl text-sm md:text-base">
            Plain-English explanation of the opt-in workflow, the messages our
            audit platform sends to authorized staff, and how to stop receiving
            them.
          </p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-8">
        {/* Section: Who we are */}
        <Section title="1. Who we are">
          <p>
            This SMS program is operated by <strong>Canales Franchise LLC</strong>{" "}
            (d/b/a Mueblería Canales), a U.S.-based furniture retailer that uses
            an internal field-audit platform to inspect its franchise stores and
            track operational quality.
          </p>
          <p className="text-sm text-stone-600">
            Website:{" "}
            <a
              href="https://www.canalesfurniture.com/"
              className="underline"
              target="_blank"
              rel="noreferrer"
            >
              canalesfurniture.com
            </a>
            {" · "}Contact:{" "}
            <a href="mailto:antonio@canalesfranchise.com" className="underline">
              antonio@canalesfranchise.com
            </a>
          </p>
        </Section>

        {/* Section: Who receives messages */}
        <Section title="2. Who receives the messages">
          <p>
            <strong>Only pre-authorized internal employees</strong> of Canales
            Franchise LLC and its franchise locations — specifically:
          </p>
          <ul className="list-disc pl-6 space-y-1 text-sm">
            <li>Store managers</li>
            <li>Regional auditors</li>
            <li>Leadership / direction</li>
            <li>Administrative staff</li>
          </ul>
          <p>
            We <strong>never</strong> message customers, leads, prospects, or any
            consumer audience. This is a strictly internal operations tool.
          </p>
        </Section>

        {/* Section: How they opt in */}
        <Section title="3. How an employee opts in" highlight>
          <p>
            Employees opt in themselves through our{" "}
            <strong>public self-service opt-in form</strong>:
          </p>
          <p>
            <a
              href="/sms-optin"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#0B1220] text-white text-sm font-medium hover:opacity-90"
              data-testid="sms-optin-cta"
            >
              <MessageSquare size={14} /> Open the SMS Opt-In form → /sms-optin
            </a>
          </p>
          <ol className="list-decimal pl-6 space-y-2 text-sm">
            <li>
              The employee visits{" "}
              <strong>https://www.canalesauditor.net/sms-optin</strong> (publicly
              accessible, no login required) and enters their own full name and
              mobile number.
            </li>
            <li>
              They must check an <strong>express-consent checkbox</strong> that
              states: message types, expected frequency, "Message &amp; data
              rates may apply", STOP / HELP instructions, and that consent is
              not a condition of employment or purchase.
            </li>
            <li>
              A company administrator then reviews the request in the internal
              admin tool (the “Users” page) and individually activates which
              categories of notifications the employee authorized:
              <ul className="list-disc pl-6 mt-1 space-y-0.5 text-stone-700">
                <li><code>sms_multa</code> — fines applied during an audit</li>
                <li><code>sms_to_do</code> — task (TO DO) assigned to me</li>
                <li><code>sms_no_followup_24h</code> — task open more than 24 h</li>
                <li><code>sms_reports</code> — audit closed / report ready</li>
                <li><code>sms_alerts</code> — general operational alerts</li>
              </ul>
            </li>
            <li>
              Until the administrator activates at least one of those toggles,{" "}
              <strong>no SMS is ever sent</strong> to that phone number.
            </li>
          </ol>
          <p className="text-sm text-stone-600">
            Additionally, new hires sign a written employment / partnership
            agreement that includes a clause authorizing operational
            notifications on their work mobile number. The web form above is the
            canonical, auditable record of consent.
          </p>
        </Section>

        {/* Section: What messages look like */}
        <Section title="4. What messages look like">
          <p>Examples of the actual messages our platform sends:</p>
          <pre className="bg-stone-100 border border-stone-200 rounded-md p-3 text-xs whitespace-pre-wrap leading-relaxed">
{`[Canales · AUD-2026-523] Reyna LaGranPlaza Store
[Multa] Image / Uniform: Is the sales rep wearing the tucked-in blue polo…
Auditor: Antonio Canales`}
          </pre>
          <pre className="bg-stone-100 border border-stone-200 rounded-md p-3 text-xs whitespace-pre-wrap leading-relaxed">
{`[Canales · TO DO assigned]
Store: Guzman Northridge Hills
Description: Replace broken Wall Art display tag (aisle 3)
Owner: Antonio Luna · Assigned by: Antonio Canales`}
          </pre>
          <pre className="bg-stone-100 border border-stone-200 rounded-md p-3 text-xs whitespace-pre-wrap leading-relaxed">
{`[Canales · 24 h Reminder]
Store: Reyna LaGranPlaza Store
Description: Catalog disorder in financing wall — not yet started.
Status: to_do · Owner: David Casique`}
          </pre>
        </Section>

        {/* Section: Frequency */}
        <Section title="5. Frequency">
          <p>
            Volume varies by audit activity — typically{" "}
            <strong>1 to 20 messages per recipient per month</strong>. Peak days
            (when several stores are audited the same day) may reach a higher
            count, but average monthly volume across the entire program does
            not exceed <strong>1,000 messages</strong>.
          </p>
        </Section>

        {/* Section: STOP / HELP */}
        <Section title="6. STOP and HELP">
          <p>
            Recipients can <strong>stop receiving SMS at any time</strong> by:
          </p>
          <ul className="list-disc pl-6 space-y-1 text-sm">
            <li>
              Replying <strong>STOP</strong> (also UNSUBSCRIBE, CANCEL, END,
              QUIT) to any message — Twilio automatically blocks the sender for
              that number, no further action required from the company.
            </li>
            <li>
              Asking the administrator to clear their phone number from the
              Users page and uncheck all notification toggles.
            </li>
          </ul>
          <p>
            Replying <strong>HELP</strong> returns a contact email and the URL
            of this page. Standard message and data rates may apply, depending
            on the recipient's mobile carrier and plan.
          </p>
        </Section>

        {/* Section: Privacy */}
        <Section title="7. Privacy">
          <p>
            Phone numbers are stored exclusively inside the company's audit
            platform database and are only used to deliver the SMS described
            above. We <strong>never</strong> sell, rent, share, or otherwise
            disclose phone numbers to third parties. See our{" "}
            <Link to="/privacy" className="underline font-medium">
              Privacy Policy
            </Link>{" "}
            and{" "}
            <Link to="/terms" className="underline font-medium">
              Terms &amp; Conditions
            </Link>{" "}
            for full details.
          </p>
        </Section>

        <div className="text-xs text-stone-500 pt-4 border-t border-stone-200">
          <span className="inline-flex items-center gap-1.5">
            <ShieldCheck size={12} /> Canales Franchise LLC · Operational SMS
            Notifications · Last updated: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
          </span>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children, highlight = false }) {
  return (
    <section
      className={`rounded-2xl p-5 md:p-7 border ${
        highlight ? "border-[#FACC15]/40 bg-amber-50/40" : "border-stone-200 bg-white"
      }`}
    >
      <h2 className="font-display text-xl md:text-2xl mb-3 text-stone-900">{title}</h2>
      <div className="space-y-3 text-stone-700 leading-relaxed text-[14.5px]">{children}</div>
    </section>
  );
}
